import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, SxProps, useMediaQuery, useTheme } from '@mui/material';
import {
  clientManagementData,
  contentManagementData,
  hideData,
  settingsData,
  coachManagementData,
  billingSectionData,
  bookingManagementData,
  staffManagementData,
} from '../../config/navBarConfig';
import { useAppSelector } from '../../redux/hooks';
import { UserType } from '../User/User';
import ManagementSection, { IManagementDataProps } from './ManagementSection';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import Logo from '../Common/Logo';
import ShortcutsSection, { ISectionDataProps } from './ShortcutsSection';
import i18n from '../../i18n';

interface INavbarProps {
  hidden: boolean;
  setHidden: React.Dispatch<React.SetStateAction<boolean>>;
}

const NavBar = ({ hidden, setHidden }: INavbarProps) => {
  const navigate = useNavigate();
  const { role: userType, coach } = useAppSelector((state) => state.coach);
  const { checkPermissions } = createPermissionCheckers();
  const localeLan = i18n.language;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  if (userType !== UserType.COACH) return null;

  const renderShortcutsSection = (data: ISectionDataProps, sectionName: string) =>
    checkPermissions(data.permissions, true) && (
      <Grid>
        <ShortcutsSection
          sectionName={sectionName}
          item={data}
          hidden={hidden}
          setHidden={setHidden}
        />
      </Grid>
    );

  const renderManagementSection = (data: IManagementDataProps[], managementName: string) =>
    data.some((item) => checkPermissions(item.permissions, true)) && (
      <Grid>
        <ManagementSection
          managementName={managementName}
          managementData={data}
          setHidden={setHidden}
        />
      </Grid>
    );

  const sideBarStyles = {
    backgroundColor: '#fff',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  };
  const getTransformValue = () => {
    if (hidden) {
      return localeLan === 'ar' ? 'translateX(100%)' : 'translateX(-100%)';
    }
    return 'translateX(0)';
  };

  const positionStyles: SxProps = {
    role: 'navigation-2',
    dataTestid: 'navbar-menu-2',
    top: 0,
    position: 'absolute',
    width: '300px',
    zIndex: 50,
    overflow: 'scroll',
    overflowX: 'hidden',
    transform: getTransformValue(),
    transition: 'transform 0.3s ease',
    height: '100vh',
    backgroundColor: '#fff',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(93,171,201,0.5)',
      borderRadius: '10px',
    },
    boxShadow: '0px 4px 40px 0px rgba(93,171,201,0.1)',
    border: '0 1px 0 2px solid rgba(93,171,201,0.5)',
  };

  if (localeLan === 'ar') {
    positionStyles.right = isSmallScreen ? '80px' : '84px';
  } else {
    positionStyles.left = isSmallScreen ? '80px' : '84px';
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      width={isSmallScreen ? '80px' : '84px'}
      boxShadow="1px 0px 0px 0px rgba(93,171,201,0.4), -1px 0px 0px 0px rgba(93,171,201,0.4)"
    >
      <Box
        minHeight="68px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        left={0}
        width={isSmallScreen ? '80px' : '84px'}
        zIndex={100}
        boxShadow={
          (!hidden &&
            '1px 0px 0px 0px rgba(93,171,201,0.4), -1px 0px 0px 0px rgba(93,171,201,0.4)') ||
          'none'
        }
      >
        <Grid
          minHeight="68px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={isSmallScreen ? '80px' : '84px'}
          zIndex={100}
          sx={{ backgroundColor: '#ffff', cursor: 'pointer' }}
          onClick={() => {
            navigate('/clients');
          }}
        >
          <Logo size={33} />
        </Grid>
      </Box>
      <Box
        height="calc(100vh - 68px)"
        display="flex"
        flexDirection="column"
        width={isSmallScreen ? '80px' : '84px'}
        sx={sideBarStyles}
        border="0 1px solid rgb(93,171,201,0.5)"
      >
        <Box
          role="navigation"
          data-testid="navbar-menu"
          top={68}
          zIndex={100}
          overflow="scroll"
          sx={sideBarStyles}
          height="calc(100vh - 68px)"
          border="0 1px solid rgb(93,171,201,0.5)"
          boxShadow={
            (!hidden &&
              '1px 0px 0px 0px rgba(93,171,201,0.4), -1px 0px 0px 0px rgba(93,171,201,0.4)') ||
            'none'
          }
        >
          <Grid display="flex" flexDirection="column">
            {renderShortcutsSection(clientManagementData[0], 'common.management.client')}
            {renderShortcutsSection(coachManagementData[0], 'common.management.coach')}
            {renderShortcutsSection(bookingManagementData[0], 'common.management.bookings')}
            {renderShortcutsSection(
              contentManagementData[0],
              'activityLibrary.management.activity'
            )}
            <Grid>
              <ShortcutsSection
                item={hideData[0]}
                sectionName={hideData[0].main}
                hidden={hidden}
                setHidden={setHidden}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={positionStyles}>
          <Grid display="flex" flexDirection="column" mt="10px" height="100vh" pb="10px">
            {renderManagementSection(clientManagementData, 'common.management.client')}
            {renderManagementSection(coachManagementData, 'common.management.coach')}
            {renderManagementSection(bookingManagementData, 'common.management.bookings')}
            {renderManagementSection(staffManagementData, 'common.management.staff')}
            {renderManagementSection(contentManagementData, 'common.management.content')}
            {coach.isPrincipalCoach &&
              renderManagementSection(billingSectionData, 'common.management.billing')}
            {renderManagementSection(settingsData, 'common.management.settings')}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(NavBar);
